import { atom, Getter } from "jotai";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { AnalyticsRepository } from "@/infrastructure/repositories/analytics/Analytics.repository";
import { AnalyticsFilterStateRepository } from "@/infrastructure/repositories/analyticsFilterState";
import {
  analyticsAtom,
  analyticsScoreCardsAtom,
  analyticsFilterInfoAtom,
  analyticsDeductionsByPurchaserAtom,
  analyticsDeductionsByTypeAndReasonAtom,
  analyticsDeductionVolumeVsGrossSalesAtom,
  analyticsDeductionsByAgingBracketsAtom,
  analyticsDisputePipelineAtom,
  analyticsDeductionsByRetailerAtom,
  isAnalyticsHydratedAtom,
  AnalyticsFilterInfo,
} from "./analytics.atoms";
import { getDefaultAnalyticsFilterInfo, resolveAnalyticsFilterInfo } from "./utils";
import { NumberFormatterService } from "@/core/services/NumberFormatter.service";
import { NumberFormatterAdapter } from "@/infrastructure/adapters/number";
import { GetAnalyticsScoreCardsUseCase } from "@/core/usecases/analytics/GetAnalyticsScoreCards.useCase";
import {
  GetDeductionsByPurchaserUseCase,
  GetDeductionsByTypeAndReasonUseCase,
  GetDeductionVolumeVsGrossSalesUseCase,
  GetDeductionsByAgingBracketsUseCase,
  GetDisputePipelineUseCase,
  GetDeductionsByRetailerUseCase,
  GetAnalyticsFilterStateUseCase,
} from "@/core/usecases/analytics";

const client = GraphQlAdapter.getInstance().getClient();
const analyticsRepo = new AnalyticsRepository(client);



const numberFormatterService = new NumberFormatterService(new NumberFormatterAdapter());

export const setAnalyticsFilterAtom = atom(null, (get, set, filterInfo: AnalyticsFilterInfo) => {
  set(analyticsAtom, { ...get(analyticsAtom), filterInfo });
});

export const setIsAnalyticsFilterAppliedAtom = atom(null, (get, set, isFilterApplied: boolean) => {
  set(analyticsAtom, { ...get(analyticsAtom), isFilterApplied });
});

export const setIsAnalyticsHydratedAtom = atom(null, (get, set, isHydrated: boolean) => {
  set(analyticsAtom, { ...get(analyticsAtom), isHydrated });
});

export const resetAnalyticsFilterAtom = atom(null, (_get, set, defaultFilterInfo: AnalyticsFilterInfo) => {
  set(analyticsAtom, {
    filterInfo: defaultFilterInfo,
    isFilterApplied: false,
    isHydrated: true,
  });
});

const getAnalyticsFilterForQuery = async (get: Getter): Promise<AnalyticsFilterInfo> => {
  let filterInfo = get(analyticsFilterInfoAtom);

  if (!get(isAnalyticsHydratedAtom)) {
    const saved = await new GetAnalyticsFilterStateUseCase(new AnalyticsFilterStateRepository()).execute();
    filterInfo = (saved as AnalyticsFilterInfo | null) ?? getDefaultAnalyticsFilterInfo();
  }

  return resolveAnalyticsFilterInfo(filterInfo);
};

const getAnalyticsQueryDates = (filterInfo: AnalyticsFilterInfo) => {
  const defaultFilter = getDefaultAnalyticsFilterInfo();

  return {
    startDate: filterInfo.checkFromDate ?? defaultFilter.checkFromDate!,
    endDate: filterInfo.checkToDate ?? defaultFilter.checkToDate!,
    purchaserIds: filterInfo.purchaser?.map(p => Number(p.value)) ?? [],
  };
};

export const fetchAnalyticsScoreCardsAtom = atom(null, async (get, set) => {
  set(analyticsScoreCardsAtom, {
    ...get(analyticsScoreCardsAtom),
    unresolvedDeductions: { ...get(analyticsScoreCardsAtom).unresolvedDeductions, isLoading: true, error: null },
    raisedForDispute: { ...get(analyticsScoreCardsAtom).raisedForDispute, isLoading: true, error: null },
    deductionsGrossSales: { ...get(analyticsScoreCardsAtom).deductionsGrossSales, isLoading: true, error: null },
  });
  try {
    const filterInfo = await getAnalyticsFilterForQuery(get);
    const { startDate, endDate, purchaserIds } = getAnalyticsQueryDates(filterInfo);

    const result = await new GetAnalyticsScoreCardsUseCase(analyticsRepo).execute({ startDate, endDate, purchaserIds });

    set(analyticsScoreCardsAtom, {
      ...get(analyticsScoreCardsAtom),
      unresolvedDeductions: {
        ...get(analyticsScoreCardsAtom).unresolvedDeductions,
        primaryValue: `$${numberFormatterService.format(Number(result.UNRESOLVED_DEDUCTIONS.primaryValue), "integer")}`,
        secondaryValue: `${result.UNRESOLVED_DEDUCTIONS.secondaryValue} ${Number(result.UNRESOLVED_DEDUCTIONS.secondaryValue) > 1 ? 'deductions' : 'deduction'}`,
        chipLabel: `${result.UNRESOLVED_DEDUCTIONS.chipLabel.includes('No') ? result.UNRESOLVED_DEDUCTIONS.chipLabel : result.UNRESOLVED_DEDUCTIONS.chipLabel + ' vs prior'}`,
        footerText: result.UNRESOLVED_DEDUCTIONS.footerText,
        trend: result.UNRESOLVED_DEDUCTIONS.trend,
        tooltipMarkdown: result.UNRESOLVED_DEDUCTIONS.tooltipMarkdown,
        isLoading: false,
        error: null,
      },
    });
    set(analyticsScoreCardsAtom, {
      ...get(analyticsScoreCardsAtom),
      raisedForDispute: {
        ...get(analyticsScoreCardsAtom).raisedForDispute,
        primaryValue: `$${numberFormatterService.format(Number(result.RAISED_FOR_DISPUTE.primaryValue), "integer")}`,
        secondaryValue: `${result.RAISED_FOR_DISPUTE.secondaryValue} ${Number(result.RAISED_FOR_DISPUTE.secondaryValue) > 1 ? 'disputes' : 'dispute'}`,
        chipLabel: result.RAISED_FOR_DISPUTE.chipLabel,
        footerText: result.RAISED_FOR_DISPUTE.footerText,
        trend: result.RAISED_FOR_DISPUTE.trend,
        tooltipMarkdown: result.RAISED_FOR_DISPUTE.tooltipMarkdown,
        isLoading: false,
        error: null,
      },
    });
    set(analyticsScoreCardsAtom, {
      ...get(analyticsScoreCardsAtom),
      deductionsGrossSales: {
        ...get(analyticsScoreCardsAtom).deductionsGrossSales,
        primaryValue: `$${numberFormatterService.format(Number(result.GROSS_SALE.primaryValue), "integer")}`,
        secondaryValue: `$${numberFormatterService.format(Number(result.GROSS_SALE.secondaryValue), "integer")} deducted`,
        chipLabel: result.GROSS_SALE.chipLabel,
        footerText: result.GROSS_SALE.footerText,
        trend: result.GROSS_SALE.trend,
        tooltipMarkdown: result.GROSS_SALE.tooltipMarkdown,
        isLoading: false,
        error: null,
      },
    });
  }
  catch (error: unknown) {
    set(analyticsScoreCardsAtom, {
      ...get(analyticsScoreCardsAtom),
      unresolvedDeductions: {
        ...get(analyticsScoreCardsAtom).unresolvedDeductions,
        isLoading: false,
        error: error as Error,
      },
      raisedForDispute: {
        ...get(analyticsScoreCardsAtom).raisedForDispute,
        isLoading: false,
        error: error as Error,
      },
      deductionsGrossSales: {
        ...get(analyticsScoreCardsAtom).deductionsGrossSales,
        isLoading: false,
        error: error as Error,
      },
    });
  }
});

export const fetchDeductionsByPurchaserAtom = atom(null, async (get, set) => {
  set(analyticsDeductionsByPurchaserAtom, {
    ...get(analyticsDeductionsByPurchaserAtom),
    isLoading: true,
    error: null,
  });
  try {
    const filterInfo = await getAnalyticsFilterForQuery(get);
    const { startDate, endDate, purchaserIds } = getAnalyticsQueryDates(filterInfo);

    const items = await new GetDeductionsByPurchaserUseCase(analyticsRepo).execute({ startDate, endDate, purchaserIds });

    set(analyticsDeductionsByPurchaserAtom, { items, isLoading: false, error: null });
  } catch (error: unknown) {
    set(analyticsDeductionsByPurchaserAtom, {
      ...get(analyticsDeductionsByPurchaserAtom),
      isLoading: false,
      error: error as Error,
    });
  }
});

export const fetchDeductionsByTypeAndReasonAtom = atom(
  null,
  async (get, set) => {
    set(analyticsDeductionsByTypeAndReasonAtom, {
      ...get(analyticsDeductionsByTypeAndReasonAtom),
      isLoading: true,
      error: null,
    });

    try {
      const filterInfo = await getAnalyticsFilterForQuery(get);
      const { startDate, endDate, purchaserIds } = getAnalyticsQueryDates(filterInfo);

      const result = await new GetDeductionsByTypeAndReasonUseCase(analyticsRepo).execute({
        startDate,
        endDate,
        purchaserIds,
      });

      set(analyticsDeductionsByTypeAndReasonAtom, {
        groups: result,
        isLoading: false,
        error: null,
      });
    } catch (error: unknown) {
      set(analyticsDeductionsByTypeAndReasonAtom, {
        ...get(analyticsDeductionsByTypeAndReasonAtom),
        isLoading: false,
        error: error as Error,
      });
    }
  }
);

export const fetchDeductionVolumeVsGrossSalesAtom = atom(null, async (get, set) => {
  set(analyticsDeductionVolumeVsGrossSalesAtom, {
    ...get(analyticsDeductionVolumeVsGrossSalesAtom),
    isLoading: true,
    error: null,
  });

  try {
    const filterInfo = await getAnalyticsFilterForQuery(get);
    const { startDate, endDate, purchaserIds } = getAnalyticsQueryDates(filterInfo);

    const items = await new GetDeductionVolumeVsGrossSalesUseCase(analyticsRepo).execute({
      startDate,
      endDate,
      purchaserIds,
    });

    set(analyticsDeductionVolumeVsGrossSalesAtom, { items, isLoading: false, error: null });
  } catch (error: unknown) {
    set(analyticsDeductionVolumeVsGrossSalesAtom, {
      ...get(analyticsDeductionVolumeVsGrossSalesAtom),
      isLoading: false,
      error: error as Error,
    });
  }
});

export const fetchDeductionsByAgingBracketsAtom = atom(null, async (get, set) => {
  set(analyticsDeductionsByAgingBracketsAtom, {
    ...get(analyticsDeductionsByAgingBracketsAtom),
    isLoading: true,
    error: null,
  });

  try {
    const filterInfo = await getAnalyticsFilterForQuery(get);
    const { startDate, endDate, purchaserIds } = getAnalyticsQueryDates(filterInfo);

    const data = await new GetDeductionsByAgingBracketsUseCase(analyticsRepo).execute({
      startDate,
      endDate,
      purchaserIds,
    });

    set(analyticsDeductionsByAgingBracketsAtom, { data, isLoading: false, error: null });
  } catch (error: unknown) {
    set(analyticsDeductionsByAgingBracketsAtom, {
      ...get(analyticsDeductionsByAgingBracketsAtom),
      isLoading: false,
      error: error as Error,
    });
  }
});

export const fetchDisputePipelineAtom = atom(null, async (get, set) => {
  set(analyticsDisputePipelineAtom, {
    ...get(analyticsDisputePipelineAtom),
    isLoading: true,
    error: null,
  });

  try {
    const filterInfo = await getAnalyticsFilterForQuery(get);
    const { startDate, endDate } = getAnalyticsQueryDates(filterInfo);

    const data = await new GetDisputePipelineUseCase(analyticsRepo).execute({
      startDate,
      endDate,
      purchaserIds: [],
    });

    set(analyticsDisputePipelineAtom, { data, isLoading: false, error: null });
  } catch (error: unknown) {
    set(analyticsDisputePipelineAtom, {
      ...get(analyticsDisputePipelineAtom),
      isLoading: false,
      error: error as Error,
    });
  }
});

export const fetchDeductionsByRetailerAtom = atom(null, async (get, set) => {
  set(analyticsDeductionsByRetailerAtom, {
    ...get(analyticsDeductionsByRetailerAtom),
    isLoading: true,
    error: null,
  });

  try {
    const filterInfo = await getAnalyticsFilterForQuery(get);
    const { startDate, endDate, purchaserIds } = getAnalyticsQueryDates(filterInfo);

    const data = await new GetDeductionsByRetailerUseCase(analyticsRepo).execute({
      startDate,
      endDate,
      purchaserIds,
    });

    set(analyticsDeductionsByRetailerAtom, { data, isLoading: false, error: null });
  } catch (error: unknown) {
    set(analyticsDeductionsByRetailerAtom, {
      ...get(analyticsDeductionsByRetailerAtom),
      isLoading: false,
      error: error as Error,
    });
  }
});
